import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined';
import DoneAllOutlinedIcon from '@mui/icons-material/DoneAllOutlined';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import DeleteSweepOutlinedIcon from '@mui/icons-material/DeleteSweepOutlined';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import AssignmentTurnedInOutlinedIcon from '@mui/icons-material/AssignmentTurnedInOutlined';

import firebase from '../../Firebase/Firebase';
import { useI18n } from '../../i18n/I18nProvider';

const DELETE_REVEAL_PX = 78;
const SWIPE_THRESHOLD_PX = 42;

const toMillis = (value) => value?.toMillis?.()
  || (typeof value?.seconds === 'number' ? value.seconds * 1000 : new Date(value || 0).getTime());

const iconFor = (type = '') => {
  if (String(type).includes('payment')) return <PaymentsOutlinedIcon />;
  if (String(type).includes('agenda') || String(type).includes('visit') || String(type).includes('task')) return <EventOutlinedIcon />;
  return <AssignmentTurnedInOutlinedIcon />;
};

const NotificationCenter = ({ userId }) => {
  const history = useHistory();
  const { language, locale, t, pick } = useI18n();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [revealedId, setRevealedId] = useState('');
  const [drag, setDrag] = useState({ id: '', offset: 0 });
  const touch = useRef({ id: '', startX: 0, startY: 0, moved: false });
  const suppressClick = useRef(false);

  useEffect(() => {
    if (!userId) {
      setItems([]);
      return undefined;
    }
    return firebase.firestore()
      .collection('users').doc(userId)
      .collection('notifications')
      .orderBy('created_at', 'desc')
      .limit(60)
      .onSnapshot((snapshot) => {
        setItems(snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
      }, (error) => console.warn('Notifications could not be loaded.', error));
  }, [userId]);

  const unread = useMemo(() => items.filter((item) => item.read !== true).length, [items]);
  const readCount = items.length - unread;

  const notificationRef = (id) => firebase.firestore().collection('users').doc(userId)
    .collection('notifications').doc(id);

  const markRead = async (notification) => {
    if (!userId || notification.read === true) return;
    await notificationRef(notification.id).update({
      read: true,
      read_at: firebase.firestore.FieldValue.serverTimestamp(),
    });
  };

  const openNotification = async (notification) => {
    if (suppressClick.current) {
      suppressClick.current = false;
      return;
    }
    if (revealedId) {
      setRevealedId('');
      return;
    }
    await markRead(notification);
    setOpen(false);
    if (notification.url) history.push(notification.url);
  };

  const deleteNotification = async (notification) => {
    if (!userId || notification.read !== true) {
      toast.info(pick('Lisez d’abord la notification avant de la supprimer.', 'Read the notification before deleting it.'));
      return;
    }
    try {
      await notificationRef(notification.id).delete();
      setRevealedId('');
      setDrag({ id: '', offset: 0 });
    } catch (error) {
      console.warn('Notification could not be deleted.', error);
      toast.error(pick('La notification n’a pas pu être supprimée.', 'The notification could not be deleted.'));
    }
  };

  const markAllRead = async () => {
    if (!userId || unread === 0) return;
    const db = firebase.firestore();
    const batch = db.batch();
    items.filter((item) => item.read !== true).slice(0, 450).forEach((item) => {
      batch.update(db.collection('users').doc(userId).collection('notifications').doc(item.id), {
        read: true,
        read_at: firebase.firestore.FieldValue.serverTimestamp(),
      });
    });
    await batch.commit();
  };

  const clearRead = async () => {
    if (!userId || readCount === 0) return;
    const db = firebase.firestore();
    const batch = db.batch();
    items.filter((item) => item.read === true).slice(0, 450).forEach((item) => {
      batch.delete(db.collection('users').doc(userId).collection('notifications').doc(item.id));
    });
    try {
      await batch.commit();
      setRevealedId('');
    } catch (error) {
      console.warn('Read notifications could not be deleted.', error);
      toast.error(pick('Les notifications lues n’ont pas pu être supprimées.', 'Read notifications could not be deleted.'));
    }
  };

  const beginSwipe = (notification, event) => {
    if (notification.read !== true) return;
    const point = event.touches?.[0];
    if (!point) return;
    touch.current = {
      id: notification.id,
      startX: point.clientX,
      startY: point.clientY,
      moved: false,
    };
    setDrag({ id: notification.id, offset: revealedId === notification.id ? -DELETE_REVEAL_PX : 0 });
  };

  const moveSwipe = (notification, event) => {
    if (notification.read !== true || touch.current.id !== notification.id) return;
    const point = event.touches?.[0];
    if (!point) return;
    const deltaX = point.clientX - touch.current.startX;
    const deltaY = point.clientY - touch.current.startY;
    if (Math.abs(deltaY) > Math.abs(deltaX) && !touch.current.moved) return;
    if (Math.abs(deltaX) > 8) touch.current.moved = true;
    if (!touch.current.moved) return;
    const base = revealedId === notification.id ? -DELETE_REVEAL_PX : 0;
    const offset = Math.max(-DELETE_REVEAL_PX, Math.min(0, base + deltaX));
    setDrag({ id: notification.id, offset });
  };

  const endSwipe = (notification) => {
    if (notification.read !== true || touch.current.id !== notification.id) return;
    const moved = touch.current.moved;
    const offset = drag.id === notification.id ? drag.offset : 0;
    if (moved) {
      suppressClick.current = true;
      window.setTimeout(() => { suppressClick.current = false; }, 250);
    }
    const shouldReveal = offset <= -SWIPE_THRESHOLD_PX;
    setRevealedId(shouldReveal ? notification.id : '');
    setDrag({ id: '', offset: 0 });
    touch.current = { id: '', startX: 0, startY: 0, moved: false };
  };

  if (!userId || typeof document === 'undefined') return null;

  const content = open ? (
    <>
      <Backdrop type="button" aria-label={t('notifications.close')} onClick={() => setOpen(false)} />
      <Panel role="dialog" aria-modal="true" aria-label={t('notifications.title')}>
        <PanelHeader>
          <div>
            <strong>{t('notifications.title')}</strong>
            <span>{unread ? t('notifications.unread', { count: unread }) : t('notifications.empty')}</span>
          </div>
          <CloseButton type="button" onClick={() => setOpen(false)} aria-label={t('notifications.close')}><CloseOutlinedIcon /></CloseButton>
        </PanelHeader>
        {(unread > 0 || readCount > 0) && (
          <Toolbar>
            {unread > 0 && <ToolbarButton type="button" onClick={markAllRead}><DoneAllOutlinedIcon /> {t('notifications.markAll')}</ToolbarButton>}
            {readCount > 0 && <ToolbarButton type="button" onClick={clearRead}><DeleteSweepOutlinedIcon /> {pick('Effacer les lues', 'Clear read')}</ToolbarButton>}
          </Toolbar>
        )}
        <SwipeHint>{pick('Sur mobile : faites glisser une notification lue vers la gauche pour la supprimer.', 'On mobile: swipe a read notification left to delete it.')}</SwipeHint>
        <List>
          {items.length === 0 ? (
            <Empty><NotificationsNoneOutlinedIcon /><strong>{t('notifications.empty')}</strong></Empty>
          ) : items.map((item) => {
            const title = language === 'en' ? (item.title_en || item.title || item.title_fr) : (item.title_fr || item.title);
            const body = language === 'en' ? (item.body_en || item.body || item.body_fr) : (item.body_fr || item.body);
            const date = toMillis(item.created_at);
            const currentOffset = drag.id === item.id
              ? drag.offset
              : revealedId === item.id ? -DELETE_REVEAL_PX : 0;
            return (
              <SwipeRow key={item.id}>
                {item.read === true && (
                  <DeleteAction type="button" onClick={() => deleteNotification(item)} aria-label={pick('Supprimer cette notification', 'Delete this notification')}>
                    <DeleteOutlineOutlinedIcon />
                    <span>{pick('Supprimer', 'Delete')}</span>
                  </DeleteAction>
                )}
                <Notice
                  role="button"
                  tabIndex={0}
                  $unread={item.read !== true}
                  $offset={currentOffset}
                  onClick={() => openNotification(item)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      openNotification(item);
                    }
                  }}
                  onTouchStart={(event) => beginSwipe(item, event)}
                  onTouchMove={(event) => moveSwipe(item, event)}
                  onTouchEnd={() => endSwipe(item)}
                >
                  <NoticeIcon>{iconFor(item.type)}</NoticeIcon>
                  <NoticeBody>
                    <strong>{title || 'Le Petit Boulot'}</strong>
                    {body && <span>{body}</span>}
                    {date > 0 && <time>{new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(date))}</time>}
                  </NoticeBody>
                  {item.read !== true ? (
                    <UnreadDot aria-label={language === 'en' ? 'Unread' : 'Non lue'} />
                  ) : (
                    <DesktopDelete
                      type="button"
                      aria-label={pick('Supprimer cette notification', 'Delete this notification')}
                      onClick={(event) => {
                        event.stopPropagation();
                        deleteNotification(item);
                      }}
                    ><DeleteOutlineOutlinedIcon /></DesktopDelete>
                  )}
                </Notice>
              </SwipeRow>
            );
          })}
        </List>
      </Panel>
    </>
  ) : null;

  return (
    <>
      <BellButton type="button" aria-label={t('notifications.title')} onClick={() => setOpen(true)}>
        <NotificationsNoneOutlinedIcon />
        {unread > 0 && <Badge>{unread > 99 ? '99+' : unread}</Badge>}
      </BellButton>
      {content ? createPortal(content, document.body) : null}
    </>
  );
};

const BellButton = styled.button`
  position: fixed;z-index:1260;top:.98rem;right:max(1rem,env(safe-area-inset-right));width:2.65rem;height:2.65rem;display:grid;place-items:center;flex:0 0 auto;border:1px solid rgba(0,0,0,.09);border-radius:50%;background:#fff;color:#151515;cursor:pointer;box-shadow:0 .35rem 1rem rgba(0,0,0,.06);svg{font-size:1.25rem;}
  @media(max-width:56rem){top:calc(env(safe-area-inset-top) + .78rem);right:max(4.35rem,calc(env(safe-area-inset-right) + 4rem));}
`;
const Badge = styled.span`position:absolute;top:-.22rem;right:-.2rem;min-width:1.15rem;height:1.15rem;display:grid;place-items:center;padding:0 .22rem;border:2px solid #fff;border-radius:999px;background:#a4352e;color:#fff;font-size:.58rem;font-weight:900;`;
const Backdrop = styled.button`position:fixed;inset:0;z-index:1490;width:100%;border:0;background:rgba(0,0,0,.42);backdrop-filter:blur(3px);`;
const Panel = styled.aside`position:fixed;z-index:1500;top:max(.65rem,env(safe-area-inset-top));right:max(.65rem,env(safe-area-inset-right));width:min(92vw,25rem);max-height:calc(100dvh - 1.3rem - env(safe-area-inset-top) - env(safe-area-inset-bottom));display:flex;flex-direction:column;overflow:hidden;border:1px solid rgba(255,255,255,.42);border-radius:1.25rem;background:#fff;box-shadow:0 1.5rem 4rem rgba(0,0,0,.28);`;
const PanelHeader = styled.header`display:flex;align-items:flex-start;justify-content:space-between;gap:.75rem;padding:1rem 1rem .7rem;>div{display:grid;gap:.15rem;}strong{font-size:1.1rem;}span{color:#746f68;font-size:.72rem;}`;
const CloseButton = styled.button`width:2.35rem;height:2.35rem;display:grid;place-items:center;border:0;border-radius:50%;background:#f3f0eb;cursor:pointer;`;
const Toolbar = styled.div`display:flex;flex-wrap:wrap;gap:.4rem;margin:0 1rem .45rem;`;
const ToolbarButton = styled.button`display:inline-flex;align-items:center;gap:.35rem;padding:.45rem .65rem;border:0;border-radius:999px;background:#f6efe4;color:#715629;font:inherit;font-size:.68rem;font-weight:850;cursor:pointer;svg{font-size:.95rem;}`;
const SwipeHint = styled.p`margin:0;padding:.1rem 1rem .55rem;color:#8b847b;font-size:.61rem;line-height:1.35;@media(min-width:56.01rem){display:none;}`;
const List = styled.div`min-height:0;flex:1;overflow-y:auto;padding:0 .65rem .75rem;`;
const SwipeRow = styled.div`position:relative;overflow:hidden;border-bottom:1px solid #eeeae3;border-radius:.85rem;background:#9d3029;`;
const DeleteAction = styled.button`position:absolute;inset:0 0 0 auto;width:${DELETE_REVEAL_PX}px;display:grid;place-items:center;align-content:center;gap:.15rem;border:0;background:#a4352e;color:#fff;font:inherit;font-size:.62rem;font-weight:850;cursor:pointer;svg{font-size:1.15rem;}`;
const Notice = styled.div`
  position:relative;z-index:1;width:100%;display:grid;grid-template-columns:auto minmax(0,1fr) auto;align-items:start;gap:.7rem;padding:.8rem;border:0;border-radius:.85rem;background:${({ $unread }) => ($unread ? '#fff9ed' : '#fff')};color:#151515;text-align:left;cursor:pointer;transform:translateX(${({ $offset }) => Number($offset || 0)}px);transition:${({ $offset }) => (Number($offset || 0) === 0 ? 'transform 150ms ease' : 'none')};touch-action:pan-y;&:hover{background:#f7f4ef;}
`;
const NoticeIcon = styled.span`width:2.15rem;height:2.15rem;display:grid;place-items:center;border-radius:.7rem;background:#151515;color:#d7b87e;svg{font-size:1.05rem;}`;
const NoticeBody = styled.span`min-width:0;display:grid;gap:.18rem;strong{font-size:.78rem;line-height:1.35;}span{color:#69645d;font-size:.69rem;line-height:1.45;overflow-wrap:anywhere;}time{color:#9a9389;font-size:.6rem;}`;
const UnreadDot = styled.span`width:.48rem;height:.48rem;margin-top:.35rem;border-radius:50%;background:#b18442;`;
const DesktopDelete = styled.button`width:1.85rem;height:1.85rem;display:grid;place-items:center;border:0;border-radius:50%;background:#f5efea;color:#8d312b;cursor:pointer;svg{font-size:1rem;}@media(max-width:56rem){display:none;}`;
const Empty = styled.div`min-height:13rem;display:grid;place-items:center;align-content:center;gap:.5rem;color:#79736b;svg{font-size:2rem;color:#b49a71;}`;

const mapStateToProps = ({ firebase: firebaseState }) => ({ userId: firebaseState.auth?.uid || '' });
export default connect(mapStateToProps)(NotificationCenter);
